import { all, call, put, takeEvery, select } from 'redux-saga/effects';
import type { IRootState } from '../..';
import { progressPercentageForDocuments } from '../../../lib/api/common/progressPercentage';
import { envelopeApi } from '../../../lib/api/envelope';
import { primeApi } from '../../../lib/api/prime';
import * as pageFrameActions from '../../pageFrame/actions';
import { errorToast, setLoading } from '../../pageFrame/actions';
import type { IEnvelopeMetadata } from '../envelopes/types';
import * as actions from './actions';
import type { IDocument } from './types';

const { ActionTypes } = actions;

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const getEnvelopeId = (state: IRootState) => state.dtm.documents.envelopeId;

export function* handleFetch(action: ReturnType<typeof actions.fetch>) {
  const { envelopeId, disableLoading } = action.payload;
  try {
    if (!disableLoading) {
      yield put(setLoading(true));
    }
    const metadata: IEnvelopeMetadata = yield call(envelopeApi.getEnvelopeMetadata, envelopeId);
    yield put(pageFrameActions.updateRecipientColorCounter(metadata.recipientColorCounter));
    yield put(actions.setDocuments(metadata.documents));
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    const fileDocuments = yield call(
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      primeApi.getFileDocuments,
      metadata.fileType,
      metadata.listingGuid ?? metadata.saleGuid
    );
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    const file: IFile = yield call(primeApi.getFile, metadata.fileType, metadata.listingGuid ?? metadata.saleGuid);
    yield put(actions.fetchDone(file, fileDocuments, metadata.documents));
  } catch {
    yield put(errorToast('Failed to fetch documents, please refresh!'));
  } finally {
    yield put(setLoading(false));
  }
}

export function* handleUploadDocument(action: ReturnType<typeof actions.uploadDocument>) {
  const { tempId: temporaryDocumentId, file } = action.payload;
  try {
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    const envelopeId = yield select(getEnvelopeId);

    // @ts-expect-error -- This code was written prior to enforcing lint rules
    const res = yield call(envelopeApi.uploadDocumentToEnvelope, envelopeId, temporaryDocumentId, file);
    if (res.status !== 201) {
      throw Error(`Non-201 Status Code of Envelope Document POST: ${res.status}`);
    }
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    const documents = yield call(envelopeApi.getEnvelopeDocumentsDTM, envelopeId);
    const addedDocument = documents.find((doc: IDocument) => doc.documentId === res.result.id);

    yield put(actions.uploadDocumentDone(action.payload.tempId, addedDocument!));
  } catch {
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    progressPercentageForDocuments[temporaryDocumentId].error = 'A problem occured, please try uploading again';
    yield put(actions.apiError('Error uploading Document'));
  }
}

export function* handleUploadPrimeDocument(action: ReturnType<typeof actions.uploadPrimeDocument>) {
  const { tempId: temporaryDocumentId, fileName, url } = action.payload;
  try {
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    const envelopeId = yield select(getEnvelopeId);
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    const res = yield call(envelopeApi.uploadPrimeDocumentToEnvelope, envelopeId, temporaryDocumentId, fileName, url);
    if (res.status !== 201) {
      throw Error(`Non-201 Status Code of Envelope Document POST: ${res.status}`);
    }

    // @ts-expect-error -- This code was written prior to enforcing lint rules
    const documents = yield call(envelopeApi.getEnvelopeDocumentsDTM, envelopeId);
    const addedDocument = documents.find((doc: IDocument) => doc.documentId === res.result.id);
    yield put(actions.uploadDocumentDone(action.payload.tempId, addedDocument!));
  } catch {
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    progressPercentageForDocuments[temporaryDocumentId].error = 'A problem occured, please try uploading again';
    yield put(actions.apiError('Error uploading Document'));
  }
}

export function* handleReorderDocuments(action: ReturnType<typeof actions.reorderDocuments>) {
  try {
    const documentIds = action.payload.map((document: IDocument) => document.documentId!);

    // @ts-expect-error -- This code was written prior to enforcing lint rules
    const envelopeId = yield select(getEnvelopeId);
    const envelopeConfig = {
      documentOrder: documentIds,
    };
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    const res = yield call(envelopeApi.reorderDocumentsInEnvelope, envelopeId, envelopeConfig);

    if (res.status !== 200) {
      throw Error(`Non-201 Status Code of Envelope PATCH: ${res.status}`);
    }
  } catch {
    yield put(actions.apiError('Error updating Document'));
  }
}

export function* handleDeleteDocument(action: ReturnType<typeof actions.deleteDocument>) {
  try {
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    const envelopeId = yield select(getEnvelopeId);
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    const res = yield call(envelopeApi.deleteDocument, envelopeId, action.payload);

    if (res.status !== 204) {
      throw Error(`Non-204 Status Code of Template Document DELETE: ${res.status}`);
    }
  } catch {
    yield put(actions.apiError('Error deleting Document'));
  }
}

export function* handleFetchEnvelopeData(action: ReturnType<typeof actions.fetchEnvelopeData>) {
  try {
    const metadata: IEnvelopeMetadata = yield call(envelopeApi.getEnvelopeMetadata, action.payload.envelopeId);
    yield put(actions.setDocuments(metadata.documents));
  } catch {
    yield put(errorToast('Failed to fetch documents, please refresh!'));
  }
}

// eslint-disable-next-line import/no-default-export -- This code was written prior to enforcing lint rules
export default function* rootSaga() {
  yield all([
    takeEvery(ActionTypes.FETCH, handleFetch),
    takeEvery(ActionTypes.UPLOAD_DOCUMENT, handleUploadDocument),
    takeEvery(ActionTypes.UPLOAD_PRIME_DOCUMENT, handleUploadPrimeDocument),
    takeEvery(ActionTypes.REORDER_DOCUMENTS, handleReorderDocuments),
    takeEvery(ActionTypes.DELETE_DOCUMENT, handleDeleteDocument),
    takeEvery(ActionTypes.FETCH_ENVELOPE_DATA, handleFetchEnvelopeData),
  ]);
}
